import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import styles from './ArticleSection.module.scss'
import ArticleCard from 'components/article/article-card/ArticleCard'
import { ArticleSectionProps } from './types'
import { Button, Heading } from '@boltenergy-be/design-system'

const ArticleSection = ({ articles, title, className, limit = 3, paginate }: ArticleSectionProps) => {
  // Local state
  const [amountToShow, setAmountToShow] = useState<number>(limit)

  // i18n
  const { t } = useTranslation('common')

  // Constants
  const articlesToShow = useMemo(() => {
    let tempArticles: any[] = articles || []

    if (paginate) {
      tempArticles = tempArticles?.slice(0, amountToShow)
    }

    // limit the output if necessary
    if (limit && !paginate) {
      tempArticles = tempArticles?.slice(0, limit)
    }

    return tempArticles
  }, [articles, paginate, limit, amountToShow])

  // Resets the pages to show when switching category
  useEffect(() => {
    if (paginate && articles.length) {
      setAmountToShow(limit)
    }
  }, [paginate, articles.length, limit])

  return (
    <section className={classNames('container', styles['article-section'], className)}>
      {title && (
        <Heading as="h2" serif variant="h3">
          {title}
        </Heading>
      )}
      <ul className={styles['card-grid']}>
        {articlesToShow?.length ? (
          articlesToShow.map((article) => (
            <li key={`${article.title}-${article.id}`}>
              <ArticleCard
                {...article}
                publishedAt={article.customPublishedAt ?? article.publishedAt}
                destination={article.destination}
                className={styles.article}
              />
            </li>
          ))
        ) : (
          <li>{t(($) => $.noArticlesFound)}</li>
        )}
      </ul>

      {paginate && (
        <div className={styles['button-group']}>
          {amountToShow > limit && (
            <Button
              disabled={amountToShow === limit}
              className={styles['show-less']}
              onClick={() => setAmountToShow(amountToShow - limit)}
              isFullwidthMobile
              variant="tertiary"
            >
              {t(($) => $.showLessArticles)}
            </Button>
          )}

          <Button
            disabled={amountToShow > articlesToShow.length}
            className={styles['show-more']}
            onClick={() => setAmountToShow(amountToShow + limit)}
            variant="secondary"
            isFullwidthMobile
          >
            {t(($) => $.showMoreArticles)}
          </Button>
        </div>
      )}
    </section>
  )
}

export default ArticleSection
